// VentureSwitcher.tsx — v4 venture/repo switcher. Pulled forward from
// Phase-9 (DEC-168 had deferred it) per operator ratification 2026-07-08:
// v1 tests Alkio against an EXTERNAL codebase, so the PWA needs to switch
// which venture/repo it's reporting on.
//
// Replaces the old NON-WIRED stub venture chip in the legacy UnifiedHeader
// (pwa/src/components/UnifiedHeader.tsx ~L70-84 — glyph + "Alkio" + chevron,
// no onClick, nothing behind it) with a real control: fetches the portfolio
// venture list, renders the current venture + a dropdown, and on selection
// switches the PWA's API base (DEC-164: each venture is its own Postgres
// instance, so switching is base-URL swap, not a tenant query param).
//
// A ventures list with no override renders the same single "Alkio" chip
// the legacy header did, so single-venture installs still look calm.

import { useEffect, useState } from "react";
import { Icons } from "../components/Icons.js";
import {
  fetchVentures,
  getSelectedVenture,
  setSelectedVenture,
  type SelectedVenture,
} from "../api/client.js";
import type { Venture } from "../api/types.js";

const HOME_VENTURE: Venture = {
  id: "alkio",
  name: "Alkio",
  slug: "alkio",
  status: "active",
  type: "platform",
  apiBase: null,
};

function glyph(name: string): string {
  return (name.trim()[0] ?? "?").toUpperCase();
}

export function VentureSwitcher() {
  const [open, setOpen] = useState(false);
  const [ventures, setVentures] = useState<Venture[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState<SelectedVenture | null>(() => getSelectedVenture());

  useEffect(() => {
    let cancelled = false;
    fetchVentures()
      .then((res) => {
        if (cancelled) return;
        setVentures(res.ventures.length > 0 ? res.ventures : [HOME_VENTURE]);
        setLoadError(!!res.error);
      })
      .catch(() => {
        if (cancelled) return;
        // Graceful degrade — the switcher still renders sanely with just
        // the current selection (or the Alkio default), it just can't offer
        // other options right now.
        setVentures(null);
        setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const currentName = selected?.name ?? HOME_VENTURE.name;

  function handleSelect(v: Venture) {
    if (v.apiBase === null) return; // not provisioned yet — not selectable
    const next: SelectedVenture | null =
      v.id === "alkio" ? null : { id: v.id, name: v.name, apiBase: v.apiBase };
    setSelectedVenture(next);
    setSelected(next);
    setOpen(false);
    // Full reload: every v4 screen fetches on mount against getApiUrl(), and
    // a reload is the simplest way to guarantee ALL of them re-fetch against
    // the newly selected instance rather than tracking down each consumer.
    window.location.reload();
  }

  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Switch venture"
        aria-expanded={open}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: "transparent",
          border: "none",
          padding: "2px 2px 2px 0",
          cursor: "pointer",
        }}
      >
        <div className="venture-glyph">{glyph(currentName)}</div>
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--text)",
            maxWidth: 90,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {currentName}
        </span>
        <Icons.ChevDown size={10} color="var(--text-dim)" />
      </button>

      {open && (
        <>
          {/* Backdrop — click-away to close */}
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 105 }}
          />
          <div
            role="menu"
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              left: 0,
              zIndex: 106,
              minWidth: 220,
              maxHeight: 320,
              overflowY: "auto",
              background: "var(--bg-elev)",
              border: "1px solid var(--border-soft)",
              borderRadius: 10,
              boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
              padding: 6,
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--text-dim)",
                padding: "4px 8px 6px",
              }}
            >
              Ventures
            </div>
            {ventures === null && (
              <div style={{ padding: "8px 8px", fontSize: 11, color: "var(--danger)" }}>
                ⚠ Unable to load venture list
              </div>
            )}
            {ventures?.map((v) => {
              const isCurrent = v.id === (selected?.id ?? "alkio");
              const unprovisioned = v.apiBase === null;
              return (
                <button
                  key={v.id}
                  role="menuitem"
                  disabled={unprovisioned}
                  onClick={() => handleSelect(v)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    textAlign: "left",
                    background: isCurrent ? "var(--accent-soft)" : "transparent",
                    border: "none",
                    borderRadius: 8,
                    padding: "8px 8px",
                    cursor: unprovisioned ? "default" : "pointer",
                    opacity: unprovisioned ? 0.45 : 1,
                  }}
                >
                  <div className="venture-glyph">{glyph(v.name)}</div>
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 12,
                      fontWeight: isCurrent ? 700 : 500,
                      color: isCurrent ? "var(--accent)" : "var(--text)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {v.name}
                  </span>
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 9,
                      color: "var(--text-dim)",
                      flexShrink: 0,
                    }}
                  >
                    {unprovisioned ? "not provisioned" : v.status}
                  </span>
                </button>
              );
            })}
            {loadError && ventures && (
              <div
                style={{
                  padding: "6px 8px 2px",
                  fontSize: 10,
                  color: "var(--warn)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                ⚠ Showing last-known list — refresh failed
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
